import { StyledTag } from '@app/components/tag/Tag';
import type { Interaction } from '@app/services/api.types';
import { fn } from '@app/utils';
import {
    Descriptions,
    Divider,
    Drawer,
    Tabs,
    type DescriptionsProps,
    type DrawerProps,
    type TabsProps,
} from 'antd';
import { startCase } from 'lodash';
import { useState } from 'react';
import InteractionAudio from './InteractionAudio';

export type InteractionDrawerProps = DrawerProps & {
    data: Interaction | null;
};

export const InteractionDrawer = ({ data, ...props }: InteractionDrawerProps) => {
    const [activeTab, setActiveTab] = useState<string>('1');

    const detailsItems: DescriptionsProps['items'] = [
        {
            key: '1',
            label: 'Interaction Type',
            children: data?.type_name || '-',
        },
        {
            key: '2',
            label: 'Duration',
            children: data?.duration ? fn.secondsToMinutesSeconds(data.duration) : 'N/A',
        },
        {
            key: '3',
            label: 'Brand',
            children: data?.brand_name || '-',
        },
        {
            key: '4',
            label: 'Office',
            children: data?.location_name || '-',
        },
    ];

    const contactItems: DescriptionsProps['items'] = [
        {
            key: '1',
            label: 'Name',
            children:
                [data?.contact_first_name, data?.contact_last_name].filter(Boolean).join(' ') ||
                '-',
        },
        {
            key: '2',
            label: 'Phone',
            children: data?.contact_number || '-',
        },
        {
            key: '3',
            label: 'Email',
            children: data?.contact_email || '-',
        },
    ];

    const outcomeItems: DescriptionsProps['items'] = [
        // {
        //     key: '1',
        //     label: 'Sales Viewing Booked',
        //     children: (
        //         <StyledTag
        //             color={data?.sales_viewing ? 'green' : 'default'}
        //             style={{ marginTop: 5, minWidth: 38 }}>
        //             {data?.sales_viewing ? 'Yes' : 'No'}
        //         </StyledTag>
        //     ),
        // },
        // {
        //     key: '2',
        //     label: 'Lettings Viewing Booked',
        //     children: (
        //         <StyledTag
        //             color={data?.lettings_viewing ? 'green' : 'default'}
        //             style={{ marginTop: 5, minWidth: 38 }}>
        //             {data?.lettings_viewing ? 'Yes' : 'No'}
        //         </StyledTag>
        //     ),
        // },
        // {
        //     key: '3',
        //     label: 'Valuation Booked',
        //     children: (
        //         <StyledTag
        //             color={data?.valuation_booked ? 'green' : 'default'}
        //             style={{ marginTop: 5, minWidth: 38 }}>
        //             {data?.valuation_booked ? 'Yes' : 'No'}
        //         </StyledTag>
        //     ),
        // },
        // {
        //     key: '4',
        //     label: 'FS Booking',
        //     children: (
        //         <StyledTag
        //             color={data?.fs_booking ? 'green' : 'default'}
        //             style={{ marginTop: 5, minWidth: 38 }}>
        //             {data?.fs_booking ? 'Yes' : 'No'}
        //         </StyledTag>
        //     ),
        // },
        // {
        //     key: '5',
        //     label: 'Call Forwarded',
        //     children: (
        //         <StyledTag
        //             color={data?.forwarded_to ? 'green' : 'default'}
        //             style={{ marginTop: 5, minWidth: 38 }}>
        //             {data?.forwarded_to ? 'Yes' : 'No'}
        //         </StyledTag>
        //     ),
        // },
        // {
        //     key: '6',
        //     label: data?.forwarded_to ? 'Forwarded To' : '',
        //     children: data?.forwarded_to || '',
        // },

        {
            key: '1',
            label: 'Call Forwarded',
            children:
                data?.type_name !== 'Call' ? (
                    'N/A'
                ) : (
                    <StyledTag
                        color={data?.forwarded_to ? 'green' : 'default'}
                        style={{ marginTop: 5, minWidth: 38 }}>
                        {data?.forwarded_to ? 'Yes' : 'No'}
                    </StyledTag>
                ),
        },
        {
            key: '2',
            label: 'Forwarded To',
            children: data?.forwarded_to || 'N/A',
        },
    ];

    const getOutcomesTabs = (outcomeItems: DescriptionsProps['items']) => {
        return outcomeItems;
        return outcomeItems!.filter((tab) => {
            if (!tab) return false;
            if (data?.type_name == 'Call') {
                return true;
            } else {
                return tab.key !== undefined && tab.key <= '4';
            }
        });
    };

    const renderDetailsTab = () => {
        return (
            <>
                <Descriptions
                    title="Details"
                    colon={false}
                    layout="vertical"
                    items={detailsItems}
                    column={2}
                />
                <Divider />
                <Descriptions
                    title="Contact Information"
                    colon={false}
                    layout="vertical"
                    items={contactItems}
                    column={2}
                />
                <Divider />
                <Descriptions
                    title="Outcomes"
                    colon={false}
                    layout="vertical"
                    items={getOutcomesTabs(outcomeItems)}
                    column={2}
                />
            </>
        );
    };

    const renderAudioTab = () => {
        if (!data?.id || !data?.recording_url) {
            return null;
        }

        return <InteractionAudio id={data?.id} url={data?.recording_url} />;
    };

    const renderTranscriptTab = () => {
        return data?.transcript?.map((transcript) => (
            <div key={transcript.created_at} className={`transcript ${transcript?.type}`}>
                <p className="transcript-title">{startCase(transcript?.type)}</p>
                <p className="transcript-content">{transcript?.content}</p>
            </div>
        ));
    };

    const items: TabsProps['items'] = [
        {
            key: '1',
            label: 'Details',
            children: renderDetailsTab(),
        },
        {
            key: '2',
            label: 'Audio',
            children: renderAudioTab(),
            disabled: !data?.recording_url,
        },
        {
            key: '3',
            label: 'Transcript',
            children: renderTranscriptTab(),
            disabled: !data?.transcript?.length,
        },
    ];

    return (
        <Drawer
            title={
                <>
                    <span>Interaction Details</span>
                    {data?.id ? (
                        <div className="ant-drawer-header-meta">
                            <span>{fn.formatDate(data?.start, 'DD/MM/YYYY, HH:mm:ss')}</span>
                        </div>
                    ) : null}
                </>
            }
            size={700}
            afterOpenChange={() => {
                if (!props.open) {
                    setActiveTab('1');
                }
            }}
            closable={{ 'aria-label': 'Close Button' }}
            {...props}>
            <Tabs
                items={items}
                activeKey={activeTab}
                onChange={(key) => setActiveTab(key)}
                className="sticky-tabs"
            />
        </Drawer>
    );
};

export default InteractionDrawer;
